import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { buildApiUrl } from '../../config/constants';
import CollectionService, { Collection, Book } from '../../services/CollectionService';

const ManageCollections: React.FC = () => {
  const [collections, setCollections] = useState<Collection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [selectedCollection, setSelectedCollection] = useState<Collection | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [allBooks, setAllBooks] = useState<Book[]>([]);
  const [booksLoading, setBooksLoading] = useState(false);
  
  const [editForm, setEditForm] = useState({
    name: '',
    description: '',
    isPublic: true,
    bookIds: [] as number[]
  });
  
  const [addForm, setAddForm] = useState({
    name: '',
    description: '',
    isPublic: true,
    bookIds: [] as number[]
  });
  
  const navigate = useNavigate();

  useEffect(() => {
    // Verificăm dacă utilizatorul este autentificat și este admin
    const userData = localStorage.getItem('user');
    const authToken = localStorage.getItem('authToken');
    
    if (!userData || !authToken) {
      navigate('/login');
      return;
    }
    
    try {
      const user = JSON.parse(userData);
      
      if (user.role !== 'admin') {
        // Dacă utilizatorul nu este admin, redirecționăm către home
        navigate('/');
        return;
      }
      
      // Încarcă colecțiile
      fetchCollections();
      // Încarcă lista de cărți pentru selecție
      fetchBooks();
    } catch (err) {
      navigate('/login');
    }
  }, [navigate]);

  const fetchCollections = async () => {
    try {
      setLoading(true);
      const data = await CollectionService.getAllCollections();
      setCollections(data);
      setError('');
    } catch (err: any) {
      setError(err.message || 'A apărut o eroare la încărcarea colecțiilor');
    } finally {
      setLoading(false);
    }
  };

  const fetchBooks = async () => {
    try {
      setBooksLoading(true);
      // Folosim API-ul cărților pentru a obține toate cărțile
      const response = await fetch(buildApiUrl('/books?limit=100'), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Eroare la încărcarea cărților');
      }
      
      const data = await response.json();
      setAllBooks(data.books || []);
    } catch (err: any) {
      console.error('Eroare la încărcarea cărților:', err);
    } finally {
      setBooksLoading(false);
    }
  };

  const showSuccessMessage = (message: string) => {
    setSuccessMessage(message);
    // Ascunde mesajul de succes după 5 secunde
    setTimeout(() => {
      setSuccessMessage('');
    }, 5000);
  };

  const openEditModal = (collection: Collection) => {
    setSelectedCollection(collection);
    setEditForm({
      name: collection.name,
      description: collection.description || '',
      isPublic: collection.isPublic,
      bookIds: collection.books ? collection.books.map(book => book.id) : []
    });
    setIsEditModalOpen(true);
  };

  const openDeleteModal = (collection: Collection) => {
    setSelectedCollection(collection);
    setIsDeleteModalOpen(true);
  };

  const openAddModal = () => {
    setAddForm({
      name: '',
      description: '',
      isPublic: true,
      bookIds: []
    });
    setIsAddModalOpen(true);
  };

  const handleEditFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const target = e.target as HTMLInputElement;
      setEditForm({
        ...editForm,
        [name]: target.checked,
      });
    } else {
      setEditForm({
        ...editForm,
        [name]: value,
      });
    }
  };

  const handleAddFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const target = e.target as HTMLInputElement;
      setAddForm({
        ...addForm,
        [name]: target.checked,
      });
    } else {
      setAddForm({
        ...addForm,
        [name]: value,
      });
    }
  };

  const handleBookSelection = (e: React.ChangeEvent<HTMLSelectElement>, formType: 'edit' | 'add') => {
    const selectedOptions = Array.from(e.target.selectedOptions);
    const selectedBookIds = selectedOptions.map(option => parseInt(option.value));
    
    if (formType === 'edit') {
      setEditForm({
        ...editForm,
        bookIds: selectedBookIds
      });
    } else {
      setAddForm({
        ...addForm,
        bookIds: selectedBookIds
      });
    }
  };

  const handleUpdateCollection = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCollection) return;
    
    try {
      setLoading(true);
      await CollectionService.updateCollection(selectedCollection.id, editForm);
      
      // Actualizează lista de colecții
      await fetchCollections();
      setIsEditModalOpen(false);
      showSuccessMessage(`Colecția "${editForm.name}" a fost actualizată cu succes!`);
    } catch (err: any) {
      setError(err.message || 'A apărut o eroare la actualizarea colecției');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCollection = async () => {
    if (!selectedCollection) return;
    
    try {
      setLoading(true);
      await CollectionService.deleteCollection(selectedCollection.id);
      
      // Actualizează lista de colecții
      const updatedCollections = collections.filter(collection => collection.id !== selectedCollection.id);
      setCollections(updatedCollections);
      setIsDeleteModalOpen(false);
      showSuccessMessage(`Colecția "${selectedCollection.name}" a fost ștearsă cu succes!`);
    } catch (err: any) {
      setError(err.message || 'A apărut o eroare la ștergerea colecției');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCollection = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      await CollectionService.addCollection(addForm);
      
      // Actualizează lista de colecții
      await fetchCollections();
      setIsAddModalOpen(false);
      showSuccessMessage(`Colecția "${addForm.name}" a fost adăugată cu succes!`);
    } catch (err: any) {
      setError(err.message || 'A apărut o eroare la adăugarea colecției');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-100">
      <Header />
      
      {/* Spațiu pentru header-ul fix */}
      <div className="pt-24 md:pt-28"></div>
      
      <main className="flex-grow pb-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {loading && collections.length === 0 ? (
            <div className="bg-white shadow rounded-lg p-12 flex justify-center items-center">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : (
            <>
          {/* Header */}
          <div className="bg-white shadow rounded-lg p-6 mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Gestionare Colecții</h1>
                <p className="mt-1 text-sm text-gray-500">
                  Administrează colecțiile de cărți în bibliotecă
                </p>
              </div>
              <div className="mt-4 sm:mt-0">
                <button
                  onClick={() => navigate('/admin/dashboard')}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Înapoi la Dashboard
                </button>
              </div>
            </div>
          </div>

          {/* Afișare eroare */}
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
              <p>{error}</p>
              <button 
                onClick={() => setError('')}
                className="float-right"
              >
                &times;
              </button>
            </div>
          )}

          {/* Afișare mesaj de succes */}
          {successMessage && (
            <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-6">
              <p>{successMessage}</p>
              <button 
                onClick={() => setSuccessMessage('')}
                className="float-right"
              >
                &times;
              </button>
            </div>
          )}

          {/* Buton adaugă colecție */}
          <div className="mb-6">
            <button
              onClick={openAddModal}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
              <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
              </svg>
              Adaugă Colecție Nouă
            </button>
          </div>

          {/* Lista colecțiilor */}
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="px-4 py-5 sm:px-6 bg-gray-50 border-b border-gray-200">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Lista Colecțiilor
              </h3>
            </div>
            {collections.length > 0 ? (
              <ul className="divide-y divide-gray-200">
                {collections.map((collection) => (
                  <li key={collection.id} className="px-4 py-4 sm:px-6">
                    <div className="flex items-center justify-between">
                      <div className="flex flex-col">
                        <h4 className="text-lg font-medium text-gray-900">{collection.name}</h4>
                        <p className="text-sm text-gray-500 mt-1">{collection.description || 'Fără descriere'}</p>
                        <div className="flex items-center mt-1">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                            ${collection.isPublic ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                            {collection.isPublic ? 'Public' : 'Privat'}
                          </span>
                          <span className="ml-2 text-sm text-gray-500">
                            {collection.bookCount || 0} cărți
                          </span>
                        </div>
                      </div>
                      <div className="flex space-x-3">
                        <button
                          onClick={() => openEditModal(collection)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          Editează
                        </button>
                        <button
                          onClick={() => openDeleteModal(collection)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Șterge
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="px-4 py-5 sm:p-6 text-center">
                <p className="text-gray-500">Nu există colecții disponibile. Adăugați prima colecție folosind butonul de mai sus.</p>
              </div>
            )}
          </div>
            </>
          )}
        </div>
      </main>
      
      {/* Modal Editare Colecție */}
      {isEditModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleUpdateCollection}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="mb-4">
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700">Nume colecție</label>
                    <input
                      type="text"
                      name="name"
                      id="name"
                      value={editForm.name}
                      onChange={handleEditFormChange}
                      required
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    />
                  </div>
                  <div className="mb-4">
                    <label htmlFor="description" className="block text-sm font-medium text-gray-700">Descriere</label>
                    <textarea
                      name="description"
                      id="description"
                      value={editForm.description}
                      onChange={handleEditFormChange}
                      rows={3}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    />
                  </div>
                  <div className="mb-4">
                    <div className="flex items-start">
                      <div className="flex items-center h-5">
                        <input
                          id="isPublic"
                          name="isPublic"
                          type="checkbox"
                          checked={editForm.isPublic}
                          onChange={handleEditFormChange}
                          className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded"
                        />
                      </div>
                      <div className="ml-3 text-sm">
                        <label htmlFor="isPublic" className="font-medium text-gray-700">Colecție publică</label>
                        <p className="text-gray-500">Dacă este bifat, colecția va fi vizibilă pentru toți utilizatorii</p>
                      </div>
                    </div>
                  </div>
                  <div className="mb-4">
                    <label htmlFor="books" className="block text-sm font-medium text-gray-700">Cărți în colecție</label>
                    <select
                      multiple
                      id="books"
                      name="books"
                      onChange={(e) => handleBookSelection(e, 'edit')}
                      value={editForm.bookIds.map(id => id.toString())}
                      className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      style={{ height: '200px' }}
                    >
                      {booksLoading ? (
                        <option>Se încarcă cărțile...</option>
                      ) : (
                        allBooks.map((book) => (
                          <option key={book.id} value={book.id}>
                            {book.title} - {book.author}
                          </option>
                        ))
                      )}
                    </select>
                    <p className="mt-1 text-xs text-gray-500">Țineți apăsat Ctrl (Windows) sau Command (Mac) pentru a selecta mai multe cărți</p>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Salvează
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsEditModalOpen(false)}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Anulează
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal Ștergere Colecție */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <svg className="h-6 w-6 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">Șterge colecție</h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Ești sigur că vrei să ștergi colecția "{selectedCollection?.name}"? Această acțiune nu poate fi anulată.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleDeleteCollection}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Șterge
                </button>
                <button
                  type="button"
                  onClick={() => setIsDeleteModalOpen(false)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Anulează
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Adaugă Colecție */}
      {isAddModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleAddCollection}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="mb-4">
                    <label htmlFor="add-name" className="block text-sm font-medium text-gray-700">Nume colecție</label>
                    <input
                      type="text"
                      name="name"
                      id="add-name"
                      value={addForm.name}
                      onChange={handleAddFormChange}
                      required
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    />
                  </div>
                  <div className="mb-4">
                    <label htmlFor="add-description" className="block text-sm font-medium text-gray-700">Descriere</label>
                    <textarea
                      name="description"
                      id="add-description"
                      value={addForm.description}
                      onChange={handleAddFormChange}
                      rows={3}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    />
                  </div>
                  <div className="mb-4">
                    <div className="flex items-start">
                      <div className="flex items-center h-5">
                        <input
                          id="add-isPublic"
                          name="isPublic"
                          type="checkbox"
                          checked={addForm.isPublic}
                          onChange={handleAddFormChange}
                          className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded"
                        />
                      </div>
                      <div className="ml-3 text-sm">
                        <label htmlFor="add-isPublic" className="font-medium text-gray-700">Colecție publică</label>
                        <p className="text-gray-500">Dacă este bifat, colecția va fi vizibilă pentru toți utilizatorii</p>
                      </div>
                    </div>
                  </div>
                  <div className="mb-4">
                    <label htmlFor="add-books" className="block text-sm font-medium text-gray-700">Cărți în colecție</label>
                    <select
                      multiple
                      id="add-books"
                      name="books"
                      onChange={(e) => handleBookSelection(e, 'add')}
                      value={addForm.bookIds.map(id => id.toString())}
                      className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      style={{ height: '200px' }}
                    >
                      {booksLoading ? (
                        <option>Se încarcă cărțile...</option>
                      ) : (
                        allBooks.map((book) => (
                          <option key={book.id} value={book.id}>
                            {book.title} - {book.author}
                          </option>
                        ))
                      )}
                    </select>
                    <p className="mt-1 text-xs text-gray-500">Țineți apăsat Ctrl (Windows) sau Command (Mac) pentru a selecta mai multe cărți</p>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Adaugă
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsAddModalOpen(false)}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Anulează
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      
      <Footer />
    </div>
  );
};

export default ManageCollections; 
